import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { getClientOrderSettings } from 'common/services/OrderService';
import { getActiveClientPropertyOptions } from 'admin/services/AdminPropertiesService';

import { ORDER_FULFILLMENT_TYPE } from 'common/stores/redux/MetaStore/MetaStoreApi.types';
import {
    DisplayType,
    DocumentContact,
    FulfillmentDetails,
    GenericRecord,
    Instructions,
    Quote,
} from 'common/types';
import { FulfillmentDistributionCenter } from 'common/types/trucks';

export interface CloneFormData {
    clientId: number;
    opsJobManager: number;
    priorityId: string;
    extPoNumber: string;
    extReferenceNumber1: string;
    extReferenceNumber2: string;
    instructions: Instructions;
    propertyId?: number | null;
    contacts: DocumentContact[];
    fulfillmentDetails: FulfillmentDetails;
    vendorDistributionCenter?: FulfillmentDistributionCenter | null;
    hasFulfillmentMethods?: boolean;
    fulfillmentMethod?: string | null;
    propertyRequired?: boolean;
}

export interface State {
    initialized: boolean;
    formData: CloneFormData;
    propertyRequired: boolean;
    originalClientId: number;
    clientProperties: DisplayType<number>[];
    clientPropertiesLoading: boolean;
    areFulfillmentDetailsComplete: boolean;
    pickupFulfillmentDetails: Partial<FulfillmentDetails>;
    deliveryFulfillmentDetails: Partial<FulfillmentDetails>;
}

const initialFormData: CloneFormData = {
    clientId: 0,
    opsJobManager: 0,
    priorityId: '',
    extPoNumber: '',
    extReferenceNumber1: '',
    extReferenceNumber2: '',
    instructions: {},
    propertyId: 0,
    contacts: [],
    fulfillmentDetails: {},
    vendorDistributionCenter: null,
    hasFulfillmentMethods: false,
    fulfillmentMethod: null,
    propertyRequired: false,
};

const initialState: State = {
    initialized: false,
    formData: initialFormData,
    propertyRequired: false,
    originalClientId: 0,
    clientProperties: [],
    clientPropertiesLoading: false,
    areFulfillmentDetailsComplete: false,
    pickupFulfillmentDetails: {},
    deliveryFulfillmentDetails: {},
};

function computeAreFulfillmentDetailsComplete(
    fulfillmentDetails: Partial<FulfillmentDetails> | undefined
): boolean {
    const { type, deliveryLine, distributionCenterId } =
        fulfillmentDetails ?? {};

    switch (type) {
        case ORDER_FULFILLMENT_TYPE.PICK_UP:
            return !!distributionCenterId;
        case ORDER_FULFILLMENT_TYPE.DELIVERY:
            return !!deliveryLine;
        default:
            return false;
    }
}

export const updateClient = createAsyncThunk(
    'orderQuoteClone/updateClient',
    async ({
        clientId,
        currentClientId,
    }: {
        clientId: number;
        currentClientId: number;
    }) => {
        if (clientId !== currentClientId) {
            const { hasFulfillmentMethods, propertyRequired } =
                await getClientOrderSettings(clientId);

            return {
                clientId,
                hasFulfillmentMethods,
                propertyRequired,
            };
        }

        return null;
    }
);

export const loadClientProperties = createAsyncThunk(
    'orderQuoteClone/loadClientProperties',
    (clientId: number): Promise<DisplayType<number>[]> =>
        getActiveClientPropertyOptions(clientId)
);

function saveSlot(state: State, type: ORDER_FULFILLMENT_TYPE) {
    if (type === ORDER_FULFILLMENT_TYPE.PICK_UP) {
        state.pickupFulfillmentDetails = {
            ...state.formData.fulfillmentDetails,
        };
    } else if (type === ORDER_FULFILLMENT_TYPE.DELIVERY) {
        state.deliveryFulfillmentDetails = {
            ...state.formData.fulfillmentDetails,
        };
    }
}

const slotForType = (state: State, type: ORDER_FULFILLMENT_TYPE) =>
    type === ORDER_FULFILLMENT_TYPE.PICK_UP
        ? state.pickupFulfillmentDetails
        : state.deliveryFulfillmentDetails;

function applyFulfillmentUpdate(
    state: State,
    fulfillmentDetails: Partial<FulfillmentDetails>
) {
    const current = state.formData.fulfillmentDetails;
    const currentType = current?.type as ORDER_FULFILLMENT_TYPE;
    const newType = (fulfillmentDetails.type ??
        currentType) as ORDER_FULFILLMENT_TYPE;

    if (newType && newType !== currentType) {
        saveSlot(state, currentType);
        const restoredSlot = slotForType(state, newType);
        state.formData.fulfillmentDetails = {
            ...restoredSlot,
            type: newType,
            typeDisplay:
                fulfillmentDetails.typeDisplay ?? restoredSlot?.typeDisplay,
        } as FulfillmentDetails;
    } else {
        state.formData.fulfillmentDetails = {
            ...current,
            ...fulfillmentDetails,
        } as FulfillmentDetails;
        saveSlot(state, newType);
    }
}

export const adminOrderQuoteClone = createSlice({
    name: 'adminOrderQuoteClone',
    initialState,
    reducers: {
        initialize: (state, action: PayloadAction<Quote>) => {
            const quote = action.payload;
            const quoteFD = quote.fulfillmentDetails;
            const type = quoteFD?.type as ORDER_FULFILLMENT_TYPE;
            const fulfillmentDetails: FulfillmentDetails = {
                type,
                typeDisplay: quoteFD?.typeDisplay,
                distributionCenterId: quoteFD?.distributionCenterId,
                name: quoteFD?.name,
                deliveryLine: quoteFD?.deliveryLine,
                city: quoteFD?.city,
                region: quoteFD?.region,
                postalCode: quoteFD?.postalCode,
                country: quoteFD?.country,
            };

            state.initialized = true;
            state.formData = {
                clientId: quote.clientId,
                opsJobManager: quote.opsJobManager,
                priorityId: quote.priorityId,
                extPoNumber: quote.extPoNumber,
                extReferenceNumber1: '',
                extReferenceNumber2: '',
                instructions: quote.instructions,
                contacts: quote.contacts,
                fulfillmentDetails,
                vendorDistributionCenter: null,
                hasFulfillmentMethods: quote.hasFulfillmentMethods,
                fulfillmentMethod: quote.fulfillmentMethod,
                propertyRequired: quote.propertyRequired,
                propertyId: quote.propertyId,
            };
            state.areFulfillmentDetailsComplete =
                computeAreFulfillmentDetailsComplete(fulfillmentDetails);
            state.propertyRequired = quote.propertyRequired ?? false;
            state.originalClientId = quote.clientId;

            if (type === ORDER_FULFILLMENT_TYPE.PICK_UP) {
                state.pickupFulfillmentDetails = fulfillmentDetails;
            } else if (type === ORDER_FULFILLMENT_TYPE.DELIVERY) {
                state.deliveryFulfillmentDetails = fulfillmentDetails;
            }
        },
        reset: () => initialState,

        updateKeyValuePair: (
            state,
            action: PayloadAction<{ key: string | string[]; value: unknown }>
        ) => {
            if (!state.initialized) {
                return;
            }

            const { key, value } = action.payload;
            const update: GenericRecord<unknown> = {};

            if (Array.isArray(key)) {
                key.forEach((k, idx) => {
                    update[k] = (value as unknown[])[idx];
                });
            } else {
                update[key] = value;
            }

            state.formData = { ...state.formData, ...update };
        },
        updateProperty: (state: State, action: PayloadAction<number>) => {
            if (!state.initialized) {
                return;
            }

            state.formData.propertyId = action.payload;
        },
        updateSpecialInstructions: (
            state: State,
            action: PayloadAction<string>
        ) => {
            if (!state.initialized) {
                return;
            }

            state.formData.instructions = {
                ...state.formData.instructions,
                special: action.payload,
            };
        },
        addContact: (state: State, action: PayloadAction<DocumentContact>) => {
            if (!state.initialized) {
                return;
            }

            const alreadyExists = state.formData.contacts?.some(
                c => c.uuid === action.payload.uuid
            );
            if (alreadyExists) return;

            state.formData.contacts = [
                ...(state.formData.contacts ?? []),
                action.payload,
            ];
        },
        removeContact: (
            state: State,
            action: PayloadAction<{ uuid: string; id: number }>
        ) => {
            if (!state.initialized) {
                return;
            }

            state.formData.contacts = state.formData.contacts?.filter(
                contact =>
                    contact.uuid !== action.payload.uuid ||
                    contact.id !== action.payload.id
            );
        },
        updateFulfillmentDetailsFromOrderFulfillment: (
            state: State,
            action: PayloadAction<{
                fulfillmentDetails?: Partial<FulfillmentDetails>;
                fulfillmentDistributionCenter?: FulfillmentDistributionCenter | null;
            }>
        ) => {
            if (!state.initialized) {
                return;
            }

            const { fulfillmentDetails: fd, fulfillmentDistributionCenter } =
                action.payload;

            if (fd !== undefined) {
                applyFulfillmentUpdate(state, {
                    name: fd.fulfillmentTo,
                    deliveryLine: fd.fulfillmentToAddress,
                    city: fd.fulfillmentToCity,
                    region: fd.fulfillmentToRegion,
                    postalCode: fd.fulfillmentToPostalCode,
                    country: fd.fulfillmentToCountry,
                    type: fd.fulfillmentTypeId,
                    typeDisplay: fd.typeDisplay,
                    residentialDelivery: fd.residentialDelivery,
                    distributionCenterId: fd.distributionCenterId,
                });
            }

            if (fulfillmentDistributionCenter !== undefined) {
                state.formData.vendorDistributionCenter =
                    fulfillmentDistributionCenter;
            }

            state.areFulfillmentDetailsComplete =
                computeAreFulfillmentDetailsComplete(
                    state.formData.fulfillmentDetails
                );
        },
    },
    extraReducers: builder => {
        builder.addCase(updateClient.fulfilled, (state, action) => {
            if (!state.initialized || !action.payload) {
                return;
            }

            const { clientId, hasFulfillmentMethods, propertyRequired } =
                action.payload;

            const fulfillmentMethod = hasFulfillmentMethods
                ? state.formData.fulfillmentMethod
                : null;

            const clientChanged = clientId !== state.originalClientId;
            state.propertyRequired = propertyRequired;
            state.formData = {
                ...state.formData,
                clientId,
                hasFulfillmentMethods,
                fulfillmentMethod,
                propertyRequired,
                propertyId: clientChanged
                    ? undefined
                    : state.formData.propertyId,
            };
        });
        builder.addCase(loadClientProperties.pending, state => {
            state.clientProperties = [];
            state.clientPropertiesLoading = true;
        });
        builder.addCase(loadClientProperties.fulfilled, (state, action) => {
            state.clientProperties = action.payload;
            state.clientPropertiesLoading = false;
        });
        builder.addCase(loadClientProperties.rejected, state => {
            state.clientProperties = [];
            state.clientPropertiesLoading = false;
        });
    },
});

export const {
    initialize,
    reset,
    updateKeyValuePair,
    addContact,
    removeContact,
    updateSpecialInstructions,
    updateProperty,
    updateFulfillmentDetailsFromOrderFulfillment,
} = adminOrderQuoteClone.actions;

export default adminOrderQuoteClone.reducer;
